import { Constants } from "@eagerdog/constants";
import { AKC_OBEDIENCE_JUMP_HEIGHT_LEVELS, AKC_RALLY_JUMP_HEIGHT_LEVELS, CKC_OBEDIENCE_JUMP_HEIGHT_LEVELS, CKC_RALLY_JUMP_HEIGHT_LEVELS } from "@eagerdog/constants";

import { RegistrationContext, RegistrationRule } from "./types";
import { fields, isAkcFunMatchExempt, isCkcCcnDog, isUkcConformationPuppy } from "./fields";
import {
  AKC_AGILITY_JUMP_HEIGHT_LEVELS,
  AKC_PREFERRED_OBEDIENCE_LEVELS,
  UKC_NOSEWORK_FINAL_RESPONSE_LEVELS,
  UKC_OBEDIENCE_JUMP_HEIGHT_LEVELS,
  UKC_PRECISION_COURSING_HEIGHT_LEVELS,
  UKC_RALLY_JUMP_HEIGHT_LEVELS,
} from "./levels";

const showsRequiringHeight = [
  Constants.show_type.agility,
  Constants.show_type.fast_cat,
  Constants.show_type.cat,
];

const registrationNumberExemptClubs = [
  Constants.sanctioning_club.CPE,
  Constants.sanctioning_club.GCR,
  Constants.sanctioning_club.GENERAL,
  Constants.sanctioning_club.CDSP,
  Constants.sanctioning_club.FH,
];

const levelIs = (ctx: RegistrationContext, levels: string[]) => !!ctx.level && levels.includes(ctx.level);

const pedigreeShows = (ctx: RegistrationContext) => {
  if (ctx.sanctioningClub === Constants.sanctioning_club.AKC) {
    if (ctx.show.show_type === Constants.show_type.conformation) return true;
    if (
      (ctx.show.show_type === Constants.show_type.obedience || ctx.show.show_type === Constants.show_type.rally_obedience)
      && ctx.show.require_breeder_sire_dam
    ) {
      return true;
    }
  }
  // CKC entry forms require breeder, sire and dam for conformation (Show Rules 8.1.1) and
  // obedience (Obedience Rules 6.2.1). A CCN (mixed/unrecognized breed) dog is exempt.
  if (isCkcPedigreeShow(ctx)) {
    return true;
  }
  return ctx.sanctioningClub === Constants.sanctioning_club.PWDCA
    && ctx.show.show_type === Constants.show_type.water_dog;
};

const CKC_PEDIGREE_SHOW_TYPES = [
  Constants.show_type.conformation,
  Constants.show_type.obedience,
  Constants.show_type.rally_obedience, // Rally Obedience Trial Rules 6.1.1 adopts the obedience entry requirements
  Constants.show_type.scent_detection, // Scent Detection Trial Rules 4.2.3(f)-(i): breeder, sire, dam, place of birth
];

const isCkcPedigreeShow = (ctx: RegistrationContext) =>
  ctx.sanctioningClub === Constants.sanctioning_club.CKC
  && CKC_PEDIGREE_SHOW_TYPES.includes(ctx.show.show_type)
  && !isCkcCcnDog(ctx);

export const registrationRules: RegistrationRule[] = [
  {
    id: "dob",
    phase: "dogSelect",
    matches: (ctx) => !isAkcFunMatchExempt(ctx),
    fields: [fields.dob],
  },
  {
    id: "height-agility-fastcat-cat",
    phase: "dogSelect",
    matches: (ctx) => showsRequiringHeight.includes(ctx.show.show_type),
    fields: [fields.heightAtWithers],
  },
  {
    id: "height-fh-rally",
    phase: "dogSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.FH && ctx.show.show_type === Constants.show_type.rally_obedience,
    fields: [fields.heightAtWithers],
  },
  {
    id: "pedigree",
    phase: "dogSelect",
    matches: (ctx) => !isAkcFunMatchExempt(ctx) && pedigreeShows(ctx),
    fields: [fields.sire, fields.dam, fields.breeder],
  },
  {
    id: "ckc-place-of-birth",
    phase: "dogSelect",
    matches: isCkcPedigreeShow,
    fields: [fields.countryOfBirth],
  },
  {
    id: "ukc-agility-jump-type",
    phase: "dogSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.UKC && ctx.show.show_type === Constants.show_type.agility,
    fields: [fields.heightAtWithers, fields.ukcJumpHeightType],
  },
  {
    id: "fh-profile",
    phase: "dogSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.FH,
    fields: [fields.fhCallName, fields.fhBreed],
  },
  {
    id: "general-profile",
    phase: "dogSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.GENERAL,
    fields: [fields.generalCallName, fields.generalBreed],
  },
  {
    id: "registration-number",
    phase: "dogSelect",
    matches: (ctx) => {
      if (registrationNumberExemptClubs.includes(ctx.sanctioningClub)) return false;
      if (ctx.sanctioningClub === Constants.sanctioning_club.ABI && ctx.show.show_type !== Constants.show_type.conformation) return false;
      if (isAkcFunMatchExempt(ctx)) return false;
      if (isUkcConformationPuppy(ctx)) return false;
      return true;
    },
    fields: [fields.registration],
  },
  {
    id: "akc-obedience-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.AKC
      && ctx.show.show_type === Constants.show_type.obedience
      && levelIs(ctx, AKC_OBEDIENCE_JUMP_HEIGHT_LEVELS),
    fields: [fields.akcObedienceJumpHeight],
  },
  {
    id: "ckc-obedience-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.CKC
      && ctx.show.show_type === Constants.show_type.obedience
      && levelIs(ctx, CKC_OBEDIENCE_JUMP_HEIGHT_LEVELS),
    fields: [fields.ckcObedienceJumpHeight],
  },
  {
    id: "ckc-rally-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.CKC
      && ctx.show.show_type === Constants.show_type.rally_obedience
      && levelIs(ctx, CKC_RALLY_JUMP_HEIGHT_LEVELS),
    fields: [fields.ckcRallyJumpHeight],
  },
  {
    // The CKC Scent Detection entry form's Division (Small / Medium / Large) is a withers-height band
    id: "ckc-scent-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.CKC
      && ctx.show.show_type === Constants.show_type.scent_detection,
    fields: [fields.heightAtWithers],
  },
  {
    id: "akc-obedience-preferred-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.AKC
      && ctx.show.show_type === Constants.show_type.obedience
      && levelIs(ctx, AKC_PREFERRED_OBEDIENCE_LEVELS),
    fields: [fields.akcPreferredJumpHeight],
  },
  {
    id: "akc-rally-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.AKC
      && ctx.show.show_type === Constants.show_type.rally_obedience
      && levelIs(ctx, AKC_RALLY_JUMP_HEIGHT_LEVELS),
    fields: [fields.akcRallyJumpHeight],
  },
  {
    id: "akc-agility-jump-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.AKC
      && ctx.show.show_type === Constants.show_type.agility
      && levelIs(ctx, AKC_AGILITY_JUMP_HEIGHT_LEVELS),
    fields: [fields.akcAgilityJumpHeight],
  },
  {
    id: "akc-cat-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.AKC && ctx.show.show_type === Constants.show_type.cat,
    fields: [fields.heightAtWithers],
  },
  {
    id: "fh-class-height",
    phase: "classSelect",
    matches: (ctx) => {
      if (ctx.sanctioningClub !== Constants.sanctioning_club.FH) return false;
      if (ctx.show.show_type === Constants.show_type.rally_obedience) return true;
      return ctx.show.show_type === Constants.show_type.agility && !!ctx.level && ctx.level !== "None";
    },
    fields: [fields.heightAtWithers],
  },
  {
    id: "ukc-rally-jump",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.UKC
      && ctx.show.show_type === Constants.show_type.rally_obedience
      && levelIs(ctx, UKC_RALLY_JUMP_HEIGHT_LEVELS),
    fields: [fields.ukcJumpHeightType, fields.heightAtWithers],
  },
  {
    id: "ukc-obedience-jump",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.UKC
      && ctx.show.show_type === Constants.show_type.obedience
      && levelIs(ctx, UKC_OBEDIENCE_JUMP_HEIGHT_LEVELS),
    fields: [fields.ukcJumpHeightType, fields.heightAtWithers],
  },
  {
    id: "ukc-precision-coursing-height",
    phase: "classSelect",
    matches: (ctx) => ctx.sanctioningClub === Constants.sanctioning_club.UKC
      && ctx.show.show_type === Constants.show_type.precision_coursing
      && levelIs(ctx, UKC_PRECISION_COURSING_HEIGHT_LEVELS),
    fields: [fields.heightAtWithers],
  },
  {
    id: "nosework-final-response",
    phase: "classSelect",
    matches: (ctx) => ctx.show.show_type === Constants.show_type.nosework && levelIs(ctx, UKC_NOSEWORK_FINAL_RESPONSE_LEVELS),
    fields: [fields.finalResponse],
  },
];
